import React, { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Container } from '../components/Container';
import { Section } from '../components/Section';
import { Reveal } from '../components/Reveal';
import { WorkHero } from '../components/work/WorkHero';
import { WorkFilters } from '../components/work/WorkFilters';
import type { WorkFilter } from '../components/work/WorkFilters';
import { WorkCard } from '../components/work/WorkCard';
import { CtaBand } from '../components/home/CtaBand';
import { AppImage } from '../components/AppImage';
import { projects } from '../data/projects';
import { workInProgressImage } from '../data/workImages';
import type { ProjectCategory } from '../types/content';
const categories: ProjectCategory[] = ['Branding', 'Web', 'Product'];
export function Work() {
  const [activeFilter, setActiveFilter] = useState<WorkFilter>('All');
  const prefersReducedMotion = useReducedMotion();
  const filters = useMemo(() => [{
    label: 'All' as WorkFilter,
    count: projects.length
  }, ...categories.map(category => ({
    label: category as WorkFilter,
    count: projects.filter(project => project.category === category).length
  }))], []);
  const visibleProjects = useMemo(() => activeFilter === 'All' ? projects : projects.filter(project => project.category === activeFilter), [activeFilter]);
  const [featured, ...rest] = visibleProjects;
  return <>
      <WorkHero data-id="element-1015" data-mp="tn3l6p7i6aa" />

      <Section bordered labelledBy="work-index-title" data-id="element-1016" data-mp="s28nfyzclbw">
        <Container data-id="element-1017" data-mp="eswhu0tuqqbm">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-1018" data-mp="etentp6bgiwq">
            <h2 id="work-index-title" className="text-[24px] tracking-[-0.02em] text-ink" data-id="element-1019" data-mp="9u1r8cpn7xgy">
              Case studies
            </h2>
            <WorkFilters filters={filters} activeFilter={activeFilter} onChange={setActiveFilter} data-id="element-1020" data-mp="2gl15h04bj1gj" />
          </div>

          <p aria-live="polite" className="mt-6 border-t border-hairline pt-5 text-small text-muted" data-id="element-1021" data-mp="fijsd322pk5i">
            Showing {visibleProjects.length}{' '}
            {visibleProjects.length === 1 ? 'project' : 'projects'}
            {activeFilter === 'All' ? ' across every discipline' : ` in ${activeFilter}`}.
          </p>

          <motion.div key={activeFilter} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-16 lg:mt-20" data-id="element-1022" data-mp="tikusmxzkzn3">
            {featured && <WorkCard project={featured} layout="featured" data-id="element-1023" data-mp="1880ly41fj73b" />}

            {rest.length > 0 && <div className="mt-20 grid gap-x-10 gap-y-20 md:grid-cols-2 lg:mt-24" data-id="element-1024" data-mp="1d4h9aw0t1hfc">
                {rest.map(project => <WorkCard key={project.slug} project={project} layout="standard" data-id="element-1025" data-mp="1h5ax7pe8yw88" />)}
              </div>}
          </motion.div>

          {visibleProjects.length === 0 && <Reveal className="mt-16 border-t border-hairline pt-10" data-id="element-1026" data-mp="36m51jg6ihu18">
              <p className="max-w-[44ch] text-body text-muted" data-id="element-1027" data-mp="31nv6d5ksetfm">
                Nothing published in this discipline yet. The thinking behind it is covered inside
                the case studies under the other filters.
              </p>
            </Reveal>}
        </Container>
      </Section>

      <Section bordered labelledBy="work-in-progress-caption" data-id="element-1028" data-mp="3pe7k4x2prke4">
        <Container data-id="element-1029" data-mp="3ld5jqcx292ak">
          <Reveal data-id="element-1030" data-mp="3lep34ce0jtgk">
            <figure data-id="element-1031" data-mp="3lfib1gopkp0a">
              <AppImage src={workInProgressImage.image} alt={workInProgressImage.alt} ratio="16:10" data-id="element-1032" data-mp="3gmp6otx5jioq" />
              <figcaption id="work-in-progress-caption" className="mt-4 text-[13px] leading-6 text-muted" data-id="element-1033" data-mp="3bn2pyai47kb6">
                {workInProgressImage.caption}
              </figcaption>
            </figure>
          </Reveal>
        </Container>
      </Section>

      <CtaBand data-id="element-1034" data-mp="3nv7esgc7fndn" />
    </>;
}